import React, { createContext, useState, useEffect, useRef, useContext } from 'react';

// Utility
import { useStripe, useElements, CardNumberElement } from '@stripe/react-stripe-js';
import * as isValid from 'utility/validation';
import { postUserAndPayment, getDiscountByCode } from 'utility/requests';
import { getRegistrationQuestions } from 'registration/RegistrationAPI';
import {
    isRegistrationRequired,
    initializeAttendee,
    formatRegistrationFormForPurchase,
} from 'registration/RegistrationUtils';
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import * as GA4 from 'analytics/ga4';
import * as MetaPixel from 'analytics/metaPixel';
import { calculateOrder, calculateDiscount, Bundle, Product } from 'calculations';
import { googleAnalytics } from 'services/googleAnalytics';
import { sourceTracker } from 'services/sourceTracking';

const CheckoutContext = createContext(null);

export const useCheckoutContext = () => {
    const context = useContext(CheckoutContext);

    if (!context) throw new Error('must wrap with CheckoutProvider');

    return context;
};

function initializeForm(event) {
    const defaultState = {
        firstName: { value: '', error: false, message: '' },
        lastName: { value: '', error: false, message: '' },
        email: { value: '', error: false, message: '' },
        emailConfirm: { value: '', error: false, message: '' },
        address: { value: '', error: false, message: '' },
        city: { value: '', error: false, message: '' },
        province: {
            value: event.country === 'Canada' ? event.province : '',
            error: false,
            message: '',
        },
        country: { value: 'CA', error: false, message: '' },
        postalCode: { value: '', error: false, message: '' },
        ccName: { value: '', error: false, message: '' },
        phone: { value: '', error: false, message: '' },
    };

    const sessionData = JSON.parse(sessionStorage.getItem(process.env.REACT_APP_CHECKOUT_KEY));

    if (sessionData) {
        if (
            sessionData.eventId === event.eventId &&
            Date.now() - sessionData.lastUpdated < 1 * 60 * 60 * 1000
        ) {
            // Restore user checkout session
            Object.keys(defaultState).forEach((p) => {
                defaultState[p].value = sessionData.form[p];
            });
        } else {
            // Clear invalid checkout form data
            sessionStorage.removeItem(process.env.REACT_APP_CHECKOUT_KEY);
        }
    }

    return defaultState;
}

export const CheckoutProvider = ({ children, ...props }) => {
    const {
        products,
        bundles,
        event,
        paidTransaction,
        showDiscountForm,
        discount,
        discountFormState,
        discountDispatch,
        onDiscountChange,
        accessCodes,
        onPurchase,
        onCancel,
    } = props;

    const { i18n } = useLingui();
    const stripe = useStripe();
    const elements = useElements();

    // State
    const [status, setStatus] = useState('Loading'); // status = Loading | Error | Registration | Checkout | Submit
    const [errorMessage, setErrorMessage] = useState('');

    const contentRef = useRef();

    // Registration State
    const [registrationEnabled, setRegistrationEnabled] = useState(
        event?.checkoutQuestionMode === 1 || event?.checkoutQuestionMode === 2,
    );
    const [registrationForm, setRegistrationForm] = useState(null);

    // Initialize State
    useEffect(() => {
        async function init() {
            if (registrationEnabled) {
                const { required, custom } = await getRegistrationQuestions(event.eventId);

                const registrationRequired = isRegistrationRequired(
                    event.checkoutQuestionMode,
                    custom,
                    required,
                    products,
                    bundles,
                );

                if (registrationRequired) {
                    const attendeeForm = initializeAttendee(
                        event.checkoutQuestionMode,
                        products,
                        bundles,
                        custom,
                        required,
                    );

                    setRegistrationEnabled(true);
                    setRegistrationForm(attendeeForm);
                    setStatus('Registration');
                } else {
                    setRegistrationEnabled(false);
                    setStatus('Checkout');
                }
            }
            // Registration is disabled. Skip to payment.
            else {
                setRegistrationEnabled(false);
                setStatus('Checkout');
            }
        }
        init().catch((e) => {
            setRegistrationEnabled(false);
            setStatus('Error');
        });
        // eslint-disable-next-line
    }, []);

    // Resets the scroll position of the browser before rendering the registration or payment forms
    useEffect(() => {
        if (status === 'Registration' || status === 'Checkout') {
            contentRef.current.scrollIntoView({ block: 'end' });
        }
    }, [status]);

    // Customer Contact Form
    const [customerForm, setCustomerForm] = useState(() => initializeForm(event));

    // Backup form values to localStorage every time the state changes
    useEffect(() => {
        sessionStorage.setItem(
            process.env.REACT_APP_CHECKOUT_KEY,
            JSON.stringify({
                eventId: event.eventId,
                lastUpdated: Date.now(),
                form: Object.keys(customerForm).reduce((p, n) => {
                    p[n] = customerForm[n].value;
                    return p;
                }, {}),
            }),
        );
    }, [customerForm, event.eventId]);

    // Credit Card Form
    const [paymentForm, setPaymentForm] = useState({
        ccNumber: { error: false },
        cvc: { error: false },
        ccExpiry: { error: false },
    });

    const handleRegistrationSubmit = () => {
        setStatus('Checkout');
    };

    const handleFormChange = (field, value) => {
        let newState = { ...customerForm };

        switch (field) {
            case 'country':
                newState = {
                    ...customerForm,
                    [field]: { ...customerForm[field], value },
                    province: { ...customerForm.province, value: '' },
                };
                break;

            case 'postalCode':
                const limits = { US: 9, CA: 6, GB: 8, AU: 4, NL: 6 };
                const limit = limits[customerForm.country.value];

                let postal = value
                    .replace(/[^0-9a-zA-Z]/g, '')
                    .slice(0, limit)
                    .toUpperCase();
                newState = { ...customerForm, [field]: { ...customerForm[field], value: postal } };
                break;

            case 'phone':
                let phone = value.replace(/[^0-9]/g, '').slice(0, 11); // We changed the max length from 10 to 11 because if people enter +1 at the start, the last number will get cut off
                newState = { ...customerForm, [field]: { ...customerForm[field], value: phone } };
                break;

            case 'email':
            case 'emailConfirm':
                let email = value.replace(/\s/g, '');
                newState = { ...customerForm, [field]: { ...customerForm[field], value: email } };
                break;

            default:
                newState = { ...customerForm, [field]: { ...customerForm[field], value } };
        }

        setCustomerForm(newState);
    };

    const handleAddressAutocomplete = (address, city, province, country, postal) => {
        setCustomerForm((s) => ({
            ...s,
            address: { value: address, error: false, message: '' },
            city: { value: city, error: false, message: '' },
            province: { value: province, error: false, message: '' },
            country: { value: country, error: false, message: '' },
            postalCode: { value: postal, error: false, message: '' },
        }));

        // TODO: We need to backup the latest form changes to localStorage.
        //       We can do this here, or take the backup logic from handleFormChange() and extract it to an effect that runs on every render
    };

    async function handleFormSubmit() {
        if (isFormValid() === false) {
            setErrorMessage(t`Please review required fields`);

            GA4.exception('Payment Error: missing fields');

            return null;
        }
        setStatus('Submit');

        // Form is valid. Processing payment
        try {
            let tokenId = null;

            // Create stripe token if user buys a paid product
            if (paidTransaction) {
                // Create token
                const cardNumberElement = elements.getElement(CardNumberElement);
                const { token, error } = await stripe.createToken(cardNumberElement, {
                    name: customerForm.ccName.value,
                });

                // Return if credit card fields are invalid
                if (error) {
                    switch (error.code) {
                        case 'incomplete_number':
                            setErrorMessage(t`Please review required fields`);
                            setPaymentForm({
                                ccNumber: { error: true },
                                cvc: { error: false },
                                ccExpiry: { error: false },
                            });
                            break;

                        case 'incomplete_cvc':
                            setErrorMessage(t`Please review required fields`);
                            setPaymentForm({
                                ccNumber: { error: false },
                                cvc: { error: true },
                                ccExpiry: { error: false },
                            });
                            break;

                        case 'incomplete_expiry':
                            setErrorMessage(t`Please review required fields`);
                            setPaymentForm({
                                ccNumber: { error: false },
                                cvc: { error: false },
                                ccExpiry: { error: true },
                            });
                            break;

                        case 'card_declined':
                            setErrorMessage(t`Your payment was declined`);
                            break;

                        default:
                            setErrorMessage(t`Payment failed. Please try again.`);
                    }

                    setStatus('Checkout');
                    return null;
                } else {
                    tokenId = token.id;
                    setPaymentForm({
                        ccNumber: { error: false },
                        cvc: { error: false },
                        ccExpiry: { error: false },
                    });
                }
            }

            // *** Pre-Calculate Order *** //
            // API won't process the order if the values passed up do not match

            const cart = bundles.concat(products).map((i) => ({
                name: i.name,
                price: i.price,
                qty: i.qty,
                paidProductCount: i.paidProductCount,
            }));

            const { discountsApplied } = calculateOrder(
                cart,
                products,
                bundles,
                event,
                discount,
                paidTransaction,
            );

            const transactionData = {
                locale: i18n.locale,
                event: { eventId: event.eventId },
                customer: {
                    country: customerForm.country.value,
                    region: customerForm.province.value,
                    email: customerForm.email.value,
                    postal: customerForm.postalCode.value,
                    city: customerForm.city.value,
                    tel: customerForm.phone.value,
                    fn: customerForm.firstName.value,
                    ln: customerForm.lastName.value,
                    sa: customerForm.address.value,
                    sourceApp: 'Storefront',
                },
                products: products
                    .filter((p) => p.qty > 0)
                    .map((p) => ({ id: p.id, quantity: p.qty, price: p.price })),
                bundles: bundles
                    .filter((p) => p.qty > 0)
                    .map((p) => ({ id: p.id, quantity: p.qty, price: p.price })),
                stripe: {
                    source: tokenId,
                    description: event.title,
                    currency: 'CAD',
                },
                discountCode: null,
                registration: formatRegistrationFormForPurchase(registrationForm),
                accessCodes: accessCodes,
                clientValues: {
                    ticketsDiscounted: discountsApplied,
                },
                sourceSlug: sourceTracker.getSourceSlug(),
            };

            if (discountFormState.status === 'success') {
                transactionData.discountCode = discount.code;
            }

            postUserAndPayment(transactionData)
                .then((data) => {
                    if (data.paid === true) {
                        onPurchase(
                            customerForm.email.value,
                            discount,
                            data.ticketCount,
                            data.tran_id,
                            `${data.appleWalletURL}&lang=${i18n.locale}`,
                            `${data.googleWalletURL}&lang=${i18n.locale}`,
                        );

                        try {
                            const { id, total, tax } = data.transaction;
                            const discountCode = discount?.code;

                            // Log purchase in google analytics and meta pixel
                            GA4.purchase(id, total, tax, discountCode);

                            googleAnalytics.purchaseCompleted(id, total, tax, discountCode);

                            if (event.metaPixelId) {
                                MetaPixel.purchase(event.metaPixelId, total);
                            }

                            sessionStorage.removeItem(process.env.REACT_APP_REGISTRATION_KEY);
                            sessionStorage.removeItem(process.env.REACT_APP_CHECKOUT_KEY);
                        } catch (e) {
                            // Don't interrupt the purchase flow, since the order already went through by this point
                            return null;
                        }
                    } else {
                        const errors = {
                            // Stripe Errors
                            incorrect_number: t`The card number entered is incorrect.`,
                            invalid_number: t`The card number entered is not a valid credit card number.`,
                            invalid_expiry_month: t`Your card's expiration month is invalid.`,
                            invalid_expiry_year: t`Your card's expiration year is invalid.`,
                            invalid_cvc: t`Your card's security code is invalid.`,
                            expired_card: t`Your card has expired.`,
                            incorrect_cvc: t`Your card's security code is incorrect.`,
                            incorrect_zip: t`Your card's zip code failed validation.`,
                            card_declined: t`Your card was declined.`,
                            missing: t`There is no card on a customer that is being charged.`,
                            processing_error: t`An error occurred while processing your credit card.`,
                            // FrontDoor+ Errors
                            bad_request: t`Please ensure your contact and payment information are correct.`,
                            sold_out: t`An item in your cart is sold out.`,
                            not_enough_stock: t`There are not enough tickets in stock to fulfill this order.`,
                            ticket_not_available: t`An item in your cart is not available for sale.`,
                            invalid_event: t`We are having issues accepting payments right now. Please try again later.`,
                            // Discount Code Errors
                            discount_invalid: t`The discount code is invalid. Please remove the code and try again.`,
                            discount_expired: t`The discount code has expired. Please remove the code and try again.`,
                            discount_limit_reached: t`The discount code has been used up. Please remove the code and try again.`,
                            discount_ticket_limit_reached: t`The discount code has been used up. Please remove the code and try again.`,
                            discount_customer_limit_reached: t`You have reached your limit for this discount code. Please remove the code and try again.`,
                            // Generic Error
                            default_error: t`Unable to process your payment.`,
                        };

                        const errorMessage = errors[data.code] || errors.default_error;

                        setErrorMessage(errorMessage);

                        setStatus('Checkout');

                        GA4.exception(`Payment Error: ${errorMessage} - ${event.title}`);
                    }
                })
                .catch(() => {
                    try {
                        setErrorMessage(t`There was a problem making your purchase`);

                        setStatus('Checkout');

                        GA4.exception(`Error - ${event.title}`, true);
                    } catch (e) {
                        return null;
                    }
                });
        } catch (err) {
            setErrorMessage(t`Something went wrong. Your purchase could not be completed.`);
            setStatus('Checkout');
        }
    }

    function isFormValid() {
        const {
            firstName,
            lastName,
            email,
            emailConfirm,
            address,
            city,
            country,
            province,
            postalCode,
            ccName,
            phone,
        } = customerForm;
        let newState = {};

        newState.firstName = isValid.name(firstName.value)
            ? { ...firstName, error: false, message: '' }
            : { ...firstName, error: true, message: t`Please enter your first name` };

        newState.lastName = isValid.name(lastName.value)
            ? { ...lastName, error: false, message: '' }
            : { ...lastName, error: true, message: t`Please enter your last name` };

        newState.email = isValid.email(email.value)
            ? { ...email, error: false, message: '' }
            : { ...email, error: true, message: t`Please enter a valid email address` };

        newState.emailConfirm = isValid.email(emailConfirm.value)
            ? { ...emailConfirm, error: false, message: '' }
            : { ...emailConfirm, error: true, message: t`Please enter a valid email address` };

        if (email.value.toLowerCase() !== emailConfirm.value.toLowerCase()) {
            newState.email = {
                ...email,
                error: true,
                message: t`The emails provided do not match`,
            };
            newState.emailConfirm = {
                ...emailConfirm,
                error: true,
                message: t`The emails provided do not match`,
            };
        }

        newState.address = isValid.address(address.value)
            ? { ...address, error: false, message: '' }
            : { ...address, error: true, message: t`Please enter your address` };

        newState.city = isValid.city(city.value)
            ? { ...city, error: false, message: '' }
            : { ...city, error: true, message: t`Please enter your city` };

        const POSTAL_ERROR = {
            US: t`Please enter a valid ZIP code`,
            CA: t`Please enter a valid postal code`,
            GB: t`Please enter a valid postcode`,
            AU: t`Please enter a valid postcode`,
            NL: t`Please enter a valid postcode`,
        };
        newState.postalCode = isValid.postalCode(postalCode.value, country.value)
            ? { ...postalCode, error: false, message: '' }
            : { ...postalCode, error: true, message: POSTAL_ERROR[country.value] };

        if (country.value !== 'CA' && country.value !== 'US') {
            // We don't ask for province if user selects a region other than US/Canada
            newState.province = { ...province, error: false, message: '' };
        } else {
            newState.province = isValid.select(province.value)
                ? { ...province, error: false, message: '' }
                : {
                      ...province,
                      error: true,
                      message:
                          country.value === 'CA'
                              ? t`Please select a Province`
                              : t`Please select a State`,
                  };
        }

        // Validate ccName only if user buying paid products
        if (paidTransaction) {
            newState.ccName = isValid.name(ccName.value)
                ? { ...ccName, error: false, message: '' }
                : { ...ccName, error: true, message: t`Please enter your name` };
        }

        newState.phone = isValid.phone(phone.value)
            ? { ...phone, error: false, message: '' }
            : { ...phone, error: true, message: t`Please enter a valid phone number` };

        setCustomerForm({ ...customerForm, ...newState });

        // Return true if form is valid
        return !Object.keys(newState).some((key) => newState[key].error);
    }

    const handleDiscountSubmit = () => {
        if (discountFormState.status === 'loading') return;
        discountDispatch({ type: 'setStatus', status: 'loading' });

        getDiscountByCode(discountFormState.code, event.eventId)
            .then((discount) => {
                // ** Discount Code Valid ** //
                if (discount.status === 'DISCOUNT_CODE_VALID') {
                    const orderBundles = bundles
                        .filter((b) => b.qty > 0)
                        .map((b) => new Bundle({ id: b.id, quantity: b.qty, price: b.price }));
                    const orderProducts = products
                        .filter((p) => p.qty > 0)
                        .map((b) => new Product({ id: b.id, quantity: b.qty, price: b.price }));
                    const lineItems = orderBundles.concat(orderProducts);

                    const { discountsApplied } = calculateDiscount({
                        discPercent: discount.percent || 0,
                        discAmount: discount.amount || 0,
                        discProductMax: discount.productMax || null,
                        discTicketMax: discount.ticketMax || null,
                        ticketsDiscounted: discount.ticketsDiscounted || 0,
                        discProducts: discount.products || [],
                        perItemDiscount: Boolean(discount.perItemDiscount),
                        lineItems: lineItems,
                        orderBundles: orderBundles,
                        orderProducts: orderProducts,
                    });

                    if (discountsApplied) {
                        // Discount was applied to the order
                        if (discount.perItemDiscount) {
                            discountDispatch({
                                type: 'setStatus',
                                status: 'success',
                                message: t`Discount applied to eligible tickets in your order.`,
                            });
                        } else {
                            discountDispatch({
                                type: 'setStatus',
                                status: 'success',
                                message: t`Discount applied`,
                            });
                        }
                        onDiscountChange(discount);
                    } else {
                        // Discount was not applied to anything in the order
                        discountDispatch({
                            type: 'setStatus',
                            status: 'error',
                            message: t`There are no eligible tickets in your order`,
                        });
                    }
                }

                // ** Discount Code Invalid ** //
                else {
                    switch (discount.status) {
                        case 'DISCOUNT_CODE_NOT_FOUND':
                            discountDispatch({
                                type: 'setStatus',
                                status: 'error',
                                message: t`Not a valid code`,
                            });
                            break;
                        case 'DISCOUNT_CODE_EXPIRED':
                            discountDispatch({
                                type: 'setStatus',
                                status: 'error',
                                message: t`This code is expired`,
                            });
                            break;
                        case 'DISCOUNT_CODE_EXHAUSTED':
                        case 'DISCOUNT_CODE_TICKETS_EXHAUSTED':
                        case 'DISCOUNT_CODE_CUSTOMER_EXHAUSTED':
                            discountDispatch({
                                type: 'setStatus',
                                status: 'error',
                                message: t`This code has been used up`,
                            });
                            break;
                        default:
                            discountDispatch({
                                type: 'setStatus',
                                status: 'error',
                                message: t`Unable to verify code`,
                            });
                            break;
                    }
                }
            })
            .catch(() => {
                discountDispatch({
                    type: 'setStatus',
                    status: 'error',
                    message: t`Unable to verify code`,
                });
            });
    };

    const handleDiscountChange = (value) => {
        discountDispatch({ type: 'setCode', code: value });
    };

    const handleDiscountDelete = () => {
        discountDispatch({ type: 'deleteCode' });
        onDiscountChange(null);
    };

    const disablePurchase = status === 'Submit';

    return (
        <CheckoutContext.Provider
            value={{
                handleDiscountChange,
                handleDiscountDelete,
                disablePurchase,
                handleDiscountSubmit,
                errorMessage,
                handleFormSubmit,
                paymentForm,
                handleRegistrationSubmit,
                handleFormChange,
                handleAddressAutocomplete,
                onCancel,
                paidTransaction,
                showDiscountForm,
                isFormValid,
                status,
                contentRef,
                registrationEnabled,
                setStatus,
                registrationForm,
                setRegistrationForm,
                event,
                customerForm,
                discount,
                discountFormState,
                bundles,
                products,
            }}
        >
            {children}
        </CheckoutContext.Provider>
    );
};
